import { useEffect, useState } from 'react';
import axios from 'axios';
import { Cpu, Plus, Edit, Trash2, BarChart3, Power, PowerOff, Image, Video, Layers, Info } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { ResolutionPricingEditor } from '../components/models/ResolutionPricingEditor';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/ai-management`;

// Safely turn any API error into a human-readable string. FastAPI 422 responses
// return `detail` as an ARRAY of objects ({type, loc, msg, ...}); rendering that
// directly in a toast crashes React ("Objects are not valid as a React child").
const getErrorMessage = (error, fallback = 'Something went wrong') => {
  const detail = error?.response?.data?.detail;
  if (!detail) return error?.message || fallback;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail.map((d) => d?.msg || (typeof d === 'string' ? d : JSON.stringify(d))).join('; ') || fallback;
  }
  if (typeof detail === 'object') return detail.msg || JSON.stringify(detail);
  return String(detail);
};

const MODEL_TYPES = [
  { value: 'reasoning', label: 'Reasoning (LLM)' },
  { value: 'image_generation', label: 'Image Generation' },
  { value: 'video_generation', label: 'Video Generation' },
  { value: 'audio_generation', label: 'Audio Generation' },
  { value: 'avatar_generation', label: 'Avatar Generation' },
];

const IMAGE_CATEGORIES = [
  { value: 'text_to_image', label: 'Text to Image' },
  { value: 'image_reference', label: 'Image Reference' },
];

const VIDEO_CATEGORIES = [
  { value: 'text_to_video', label: 'Text to Video' },
  { value: 'image_to_video', label: 'Image to Video' },
  { value: 'start_end_frame_video', label: 'Start-End Frame Video' },
  { value: 'references_to_video', label: 'References to Video' },
  { value: 'motion_control_video', label: 'Motion Control / Animate Video' },
];

export default function AIModels() {
  const [models, setModels] = useState([]);
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingModel, setEditingModel] = useState(null);
  const [activeTab, setActiveTab] = useState('all');
  const [formData, setFormData] = useState({
    provider_id: '',
    model_name: '',
    model_type: 'reasoning',
    category: '',
    capabilities: '',
    max_input_limit: '',
    max_output_limit: '',
    max_shot_duration_seconds: '',
    avg_latency_ms: '',
    quality_score: '',
    cost_per_unit: '',
    credits_per_second: '',
    resolution_pricing: [],
    default_resolution: '720p',
    description: '',
  });

  useEffect(() => {
    fetchProviders();
    fetchModels();
  }, []);

  const fetchProviders = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.get(`${API}/providers`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setProviders(response.data.filter(p => p.status === 'active'));
    } catch (error) {
      console.error('Error fetching providers:', error);
    }
  };

  const fetchModels = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('admin_token');
      const response = await axios.get(`${API}/models`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setModels(response.data);
    } catch (error) {
      console.error('Error fetching models:', error);
      toast.error('Failed to fetch models');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('admin_token');
      const submitData = {
        ...formData,
        capabilities: formData.capabilities ? formData.capabilities.split(',').map(c => c.trim()) : [],
        max_input_limit: formData.max_input_limit ? parseInt(formData.max_input_limit) : null,
        max_output_limit: formData.max_output_limit ? parseInt(formData.max_output_limit) : null,
        max_shot_duration_seconds: formData.max_shot_duration_seconds ? parseInt(formData.max_shot_duration_seconds) : null,
        avg_latency_ms: formData.avg_latency_ms ? parseInt(formData.avg_latency_ms) : null,
        quality_score: formData.quality_score ? parseInt(formData.quality_score) : null,
        cost_per_unit: formData.model_type === 'video_generation' ? null : (formData.cost_per_unit ? parseFloat(formData.cost_per_unit) : null),
        credits_per_second: formData.model_type === 'video_generation' && formData.credits_per_second !== '' ? parseFloat(formData.credits_per_second) : null,
        resolution_pricing: formData.model_type === 'video_generation'
          ? formData.resolution_pricing.map((r) => ({
              resolution: r.resolution,
              credits_per_second: r.credits_per_second === '' ? null : parseFloat(r.credits_per_second),
            }))
          : null,
        default_resolution: formData.model_type === 'video_generation' ? formData.default_resolution : null,
        category: formData.category || null,
      };

      if (editingModel) {
        await axios.put(`${API}/models/${editingModel.model_id}`, submitData, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success('Model updated successfully');
      } else {
        await axios.post(`${API}/models`, submitData, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success('Model created successfully');
      }
      
      setShowDialog(false);
      resetForm();
      fetchModels();
    } catch (error) {
      console.error('Error saving model:', error);
      toast.error(getErrorMessage(error, 'Failed to save model'));
    }
  };

  const handleEdit = (model) => {
    setEditingModel(model);
    setFormData({
      provider_id: model.provider_id,
      model_name: model.model_name,
      model_type: model.model_type,
      category: model.category || '',
      capabilities: model.capabilities.join(', '),
      max_input_limit: model.max_input_limit || '',
      max_output_limit: model.max_output_limit || '',
      max_shot_duration_seconds: model.max_shot_duration_seconds || '',
      avg_latency_ms: model.avg_latency_ms || '',
      quality_score: model.quality_score || '',
      cost_per_unit: model.cost_per_unit || '',
      credits_per_second: model.credits_per_second ?? '',
      resolution_pricing: (model.resolution_pricing || []).map((r) => ({ ...r, credits_per_second: r.credits_per_second ?? '' })),
      default_resolution: model.default_resolution || '720p',
      description: model.description || '',
    });
    setShowDialog(true);
  };

  const handleDelete = async (modelId) => {
    if (!confirm('Are you sure you want to delete this model?')) return;
    
    try {
      const token = localStorage.getItem('admin_token');
      await axios.delete(`${API}/models/${modelId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Model deleted successfully');
      fetchModels();
    } catch (error) {
      console.error('Error deleting model:', error);
      toast.error('Failed to delete model');
    }
  };

  const resetForm = () => {
    setFormData({
      provider_id: '',
      model_name: '',
      model_type: 'reasoning',
      category: '',
      capabilities: '',
      max_input_limit: '',
      max_output_limit: '',
      max_shot_duration_seconds: '',
      avg_latency_ms: '',
      quality_score: '',
      cost_per_unit: '',
    credits_per_second: '',
    resolution_pricing: [],
    default_resolution: '720p',
      description: '',
    });
    setEditingModel(null);
  };

  const categorizeModels = () => {
    const categorized = {
      image: {
        text_to_image: [],
        image_reference: [],
        uncategorized: []
      },
      video: {
        text_to_video: [],
        image_to_video: [],
        start_end_frame_video: [],
        references_to_video: [],
        motion_control_video: [],
        uncategorized: []
      },
      other: []
    };

    models.forEach(model => {
      if (model.model_type === 'image_generation') {
        if (model.category && categorized.image[model.category]) {
          categorized.image[model.category].push(model);
        } else {
          categorized.image.uncategorized.push(model);
        }
      } else if (model.model_type === 'video_generation') {
        if (model.category && categorized.video[model.category]) {
          categorized.video[model.category].push(model);
        } else {
          categorized.video.uncategorized.push(model);
        }
      } else {
        categorized.other.push(model);
      }
    });

    return categorized;
  };

  const getCategoryLabel = (category) => {
    const allCategories = [...IMAGE_CATEGORIES, ...VIDEO_CATEGORIES];
    const found = allCategories.find(c => c.value === category);
    return found ? found.label : category;
  };

  const renderModelCard = (model) => (
    <div key={model.model_id} className="bg-white border rounded-lg p-4 hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-2">
            <h3 className="font-semibold text-lg">{model.model_name}</h3>
            <span className={`px-2 py-0.5 text-xs rounded-full ${
              model.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'
            }`}>
              {model.status}
            </span>
            {model.category && (
              <span className="px-2 py-0.5 text-xs rounded-full bg-blue-100 text-blue-700">
                {getCategoryLabel(model.category)}
              </span>
            )}
          </div>
          
          <p className="text-sm text-gray-600 mb-2">{model.provider_name}</p>
          
          {model.description && (
            <p className="text-sm text-gray-500 mb-2">{model.description}</p>
          )}
          
          <div className="flex flex-wrap gap-2 text-xs text-gray-500">
            {model.capabilities.length > 0 && (
              <span className="bg-gray-100 px-2 py-1 rounded">
                {model.capabilities.join(', ')}
              </span>
            )}
            {model.quality_score && (
              <span className="bg-yellow-100 text-yellow-700 px-2 py-1 rounded">
                Quality: {model.quality_score}/10
              </span>
            )}
            {model.model_type === 'video_generation' ? (
              <span data-testid={`model-credits-per-second-${model.model_id}`} className="bg-purple-100 text-purple-700 px-2 py-1 rounded">
                {(model.resolution_pricing || []).filter((r) => r.credits_per_second != null).length
                  ? model.resolution_pricing.filter((r) => r.credits_per_second != null)
                      .map((r) => `${r.resolution}: ${r.credits_per_second}/s${r.resolution === model.default_resolution ? ' (default)' : ''}`).join(' · ')
                  : model.credits_per_second != null ? `${model.credits_per_second} credits/second` : 'Not priced (50 credits/second default)'}
              </span>
            ) : model.cost_per_unit && (
              <span className="bg-purple-100 text-purple-700 px-2 py-1 rounded">
                {model.cost_per_unit} credits/unit
              </span>
            )}
          </div>

          {model.input_contract && (
            <div
              data-testid={`model-input-contract-${model.model_id}`}
              className="mt-3 rounded-md border border-amber-200 bg-amber-50/70 px-3 py-2"
            >
              <div className="flex items-center gap-1.5 mb-1">
                <Info className="h-3.5 w-3.5 text-amber-600 shrink-0" />
                <span className="text-xs font-semibold text-amber-800">
                  Input contract — {model.input_contract.summary}
                </span>
              </div>
              <p className="text-xs text-amber-900/90">
                <span className="font-medium">References:</span> {model.input_contract.reference_field}
              </p>
              {model.input_contract.prompt_binding && (
                <p className="text-xs text-amber-900/80 mt-0.5">{model.input_contract.prompt_binding}</p>
              )}
              {model.input_contract.note && (
                <p className="text-xs text-amber-700 mt-0.5">⚠ {model.input_contract.note}</p>
              )}
            </div>
          )}
        </div>
        
        <div className="flex gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => handleEdit(model)}
            data-testid={`model-edit-${model.model_id}`}
          >
            <Edit className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => handleDelete(model.model_id)}
            data-testid={`model-delete-${model.model_id}`}
          >
            <Trash2 className="h-4 w-4 text-red-500" />
          </Button>
        </div>
      </div>
    </div>
  );

  const renderCategorySection = (title, models, icon) => {
    if (models.length === 0) return null;
    
    return (
      <div className="mb-6">
        <div className="flex items-center gap-2 mb-3">
          {icon}
          <h3 className="text-md font-semibold text-gray-700">{title}</h3>
          <span className="text-sm text-gray-500">({models.length})</span>
        </div>
        <div className="grid gap-3">
          {models.map(renderModelCard)}
        </div>
      </div>
    );
  };

  const categorized = categorizeModels();

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Cpu className="h-6 w-6" />
            AI Models
          </h1>
          <p className="text-gray-600 text-sm mt-1">
            Manage AI models organized by categories
          </p>
        </div>
        <Button onClick={() => { resetForm(); setShowDialog(true); }}>
          <Plus className="h-4 w-4 mr-2" />
          Add Model
        </Button>
      </div>

      {loading ? (
        <div className="text-center py-12">Loading...</div>
      ) : (
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6">
            <TabsTrigger value="all">All Models ({models.length})</TabsTrigger>
            <TabsTrigger value="image">
              <Image className="h-4 w-4 mr-2" />
              Image ({categorized.image.text_to_image.length + categorized.image.image_reference.length + categorized.image.uncategorized.length})
            </TabsTrigger>
            <TabsTrigger value="video">
              <Video className="h-4 w-4 mr-2" />
              Video ({Object.values(categorized.video).reduce((sum, arr) => sum + arr.length, 0)})
            </TabsTrigger>
            <TabsTrigger value="other">Other ({categorized.other.length})</TabsTrigger>
          </TabsList>

          <TabsContent value="all">
            <div className="grid gap-3">
              {models.map(renderModelCard)}
            </div>
          </TabsContent>

          <TabsContent value="image">
            {renderCategorySection('Text to Image Models', categorized.image.text_to_image, <Layers className="h-5 w-5 text-blue-500" />)}
            {renderCategorySection('Image Reference Models', categorized.image.image_reference, <Layers className="h-5 w-5 text-purple-500" />)}
            {renderCategorySection('Uncategorized Image Models', categorized.image.uncategorized, <Layers className="h-5 w-5 text-gray-500" />)}
          </TabsContent>

          <TabsContent value="video">
            {renderCategorySection('Text to Video Models', categorized.video.text_to_video, <Video className="h-5 w-5 text-green-500" />)}
            {renderCategorySection('Image to Video Models', categorized.video.image_to_video, <Video className="h-5 w-5 text-teal-500" />)}
            {renderCategorySection('Start-End Frame Video Models', categorized.video.start_end_frame_video, <Video className="h-5 w-5 text-indigo-500" />)}
            {renderCategorySection('References to Video Models', categorized.video.references_to_video, <Video className="h-5 w-5 text-amber-500" />)}
            {renderCategorySection('Motion Control / Animate Video Models', categorized.video.motion_control_video, <Video className="h-5 w-5 text-pink-500" />)}
            {renderCategorySection('Uncategorized Video Models', categorized.video.uncategorized, <Video className="h-5 w-5 text-gray-500" />)}
          </TabsContent>

          <TabsContent value="other">
            <div className="grid gap-3">
              {categorized.other.map(renderModelCard)}
            </div>
          </TabsContent>
        </Tabs>
      )}

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingModel ? 'Edit Model' : 'Add New Model'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label>Provider</Label>
              <Select
                value={formData.provider_id}
                onValueChange={(value) => setFormData({ ...formData, provider_id: value })}
                required
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select provider" />
                </SelectTrigger>
                <SelectContent>
                  {providers.map((provider) => (
                    <SelectItem key={provider.provider_id} value={provider.provider_id}>
                      {provider.provider_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Model Name</Label>
              <Input
                value={formData.model_name}
                onChange={(e) => setFormData({ ...formData, model_name: e.target.value })}
                required
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Model Type</Label>
                <Select
                  value={formData.model_type}
                  onValueChange={(value) => setFormData({ ...formData, model_type: value, category: '' })}
                  required
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {MODEL_TYPES.map((type) => (
                      <SelectItem key={type.value} value={type.value}>
                        {type.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Category (Optional)</Label>
                <Select
                  value={formData.category}
                  onValueChange={(value) => setFormData({ ...formData, category: value })}
                  disabled={formData.model_type !== 'image_generation' && formData.model_type !== 'video_generation'}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={
                      formData.model_type === 'image_generation' || formData.model_type === 'video_generation'
                        ? "Select category (optional)"
                        : "No categories available for this model type"
                    } />
                  </SelectTrigger>
                  <SelectContent>
                    {formData.model_type === 'image_generation' && IMAGE_CATEGORIES.map((cat) => (
                      <SelectItem key={cat.value} value={cat.value}>
                        {cat.label}
                      </SelectItem>
                    ))}
                    {formData.model_type === 'video_generation' && VIDEO_CATEGORIES.map((cat) => (
                      <SelectItem key={cat.value} value={cat.value}>
                        {cat.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label>Capabilities (comma-separated)</Label>
              <Input
                value={formData.capabilities}
                onChange={(e) => setFormData({ ...formData, capabilities: e.target.value })}
                placeholder="e.g., high-quality, fast, watermark-free"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Quality Score (1-10)</Label>
                <Input
                  type="number"
                  min="1"
                  max="10"
                  value={formData.quality_score}
                  onChange={(e) => setFormData({ ...formData, quality_score: e.target.value })}
                />
              </div>
              {formData.model_type === 'video_generation' ? (
                <div>
                  <Label>Fallback Credits per Second</Label>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    data-testid="model-credits-per-second-input"
                    value={formData.credits_per_second}
                    onChange={(e) => setFormData({ ...formData, credits_per_second: e.target.value })}
                    placeholder="e.g., 90"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Used only if no resolution price below applies. Blank = 50/second.
                  </p>
                </div>
              ) : (
                <div>
                  <Label>Credits per Unit</Label>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    data-testid="model-credits-per-unit-input"
                    value={formData.cost_per_unit}
                    onChange={(e) => setFormData({ ...formData, cost_per_unit: e.target.value })}
                    placeholder="e.g., 10"
                  />
                </div>
              )}
            </div>

            {formData.model_type === 'video_generation' && (
              <ResolutionPricingEditor
                rows={formData.resolution_pricing}
                defaultResolution={formData.default_resolution}
                onChange={(rows, def) => setFormData({ ...formData, resolution_pricing: rows, default_resolution: def })}
              />
            )}

            <div>
              <Label>Max Shot Duration (seconds) — video models only</Label>
              <Input
                type="number"
                min="1"
                max="120"
                data-testid="model-max-shot-duration-input"
                value={formData.max_shot_duration_seconds}
                onChange={(e) => setFormData({ ...formData, max_shot_duration_seconds: e.target.value })}
                placeholder="e.g., 10 or 15 — caps per-shot length during script generation"
              />
              <p className="text-xs text-gray-500 mt-1">
                For video models. Script generation caps each shot to this value (leave blank for no cap).
              </p>
            </div>

            <div>
              <Label>Description</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={3}
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowDialog(false)}>
                Cancel
              </Button>
              <Button type="submit" data-testid="model-form-submit">
                {editingModel ? 'Update Model' : 'Create Model'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
